import * as v from 'valibot';

interface IssueStats {
  path: string;
  message: string;
  expected: string;
  receivedSamples: Set<any>;
  count: number;
}

interface SchemaSummary {
  issues: Map<string, IssueStats>; // key: path + message + expected
  totalCount: number;
  firstOccurrenceStack?: string;
  lastSeen: number;
}

interface SourceSummary {
  schemasMap: Map<v.BaseSchema<unknown, unknown, v.BaseIssue<unknown>>, SchemaSummary>;
}

/**
 * Consolidates repeat schema validation errors to prevent console spam.
 * Groups by Source -> Schema -> Property causing the error.
 * Logs periodic summaries.
 */
export class SchemaLogger {
  private static get reportIntervalMs() {
    return 300000;
  }

  private get isBypassed(): boolean {
    try {
      const isExplicitBypass = (process as any)?.env?.BYPASS_SCHEMA_LOGGER === 'true';
      return isExplicitBypass;
    } catch {
      return false;
    }
  }

  private readonly statsMap = new Map<string, SourceSummary>();
  private timer: any = null;

  /**
   * Logs a valibot validation failure.
   * Reports immediately on the first occurrence of a specific schema/source combination,
   * then consolidates subsequent errors into periodic summaries.
   */
  public log(
    source: string,
    schema: v.BaseSchema<unknown, unknown, v.BaseIssue<unknown>>,
    result: v.SafeParseResult<any>,
    data: unknown,
    schemaName?: string,
  ) {
    if (result.success) {return;}

    if (this.isBypassed) {
      console.error(`schema validation error: ${source}`, result, data);
      return;
    }

    let sourceSummary = this.statsMap.get(source);
    if (!sourceSummary) {
      sourceSummary = {schemasMap: new Map()};
      this.statsMap.set(source, sourceSummary);
    }

    let summary = sourceSummary.schemasMap.get(schema);
    if (!summary) {
      // Store schema name if provided or not already set
      if (schemaName && !(schema as any).schemaName) {
        (schema as any).schemaName = schemaName;
      }

      summary = {
        issues: new Map(),
        totalCount: 0,
        firstOccurrenceStack: new Error().stack,
        lastSeen: Date.now(),
      };
      sourceSummary.schemasMap.set(schema, summary);

      // Log immediately on first occurrence
      this.reportImmediate(source, schema, result, data);
    }

    summary.totalCount++;
    summary.lastSeen = Date.now();

    for (const issue of result.issues) {
      const path = this.normalizePath(issue.path);
      // Key includes path and expected to distinguish different issues on same path
      const key = `${path}::${issue.message}::${issue.expected}`;

      let issueStats = summary.issues.get(key);
      if (!issueStats) {
        issueStats = {
          path,
          message: issue.message,
          expected: String(issue.expected),
          receivedSamples: new Set(),
          count: 0,
        };
        summary.issues.set(key, issueStats);
      }

      issueStats.count++;
      // Limit number of distinct received samples
      if (issueStats.receivedSamples.size < 5) {
        issueStats.receivedSamples.add(this.pruneReceivedValue(issue.received));
      }
    }

    this.scheduleSummary();
  }

  private scheduleSummary() {
    if (this.timer) {return;}

    this.timer = setTimeout(() => {
      this.reportSummaries();
      this.timer = null;
    }, SchemaLogger.reportIntervalMs);
  }

  private reportImmediate(
    source: string,
    schema: v.BaseSchema<unknown, unknown, v.BaseIssue<unknown>>,
    result: v.SafeParseResult<any>,
    data: unknown,
  ) {
    if (result.success) {return;}

    const schemaName = (schema as any).schemaName || (schema as any).description || 'Anonymous Schema';
    const label = `[SchemaLogger] INITIAL ERROR: ${source} - ${schemaName}`;

    console.groupCollapsed(label);
    console.error(`First seen at ${new Date().toLocaleTimeString()}`);
    console.log('Issues:', result.issues);
    console.dir({
      source,
      schema,
      data,
    });
    console.groupEnd();
  }

  private reportSummaries() {
    if (this.statsMap.size === 0) {return;}

    const time = new Date().toLocaleTimeString();
    console.group(`%c[SchemaLogger] Consolidated Validation Report - ${time}`, 'color: #3498db; font-weight: bold; font-size: 11px;');

    this.statsMap.forEach((sourceSummary, source) => {
      console.group(`%c${source}`, 'color: #e67e22; font-weight: bold;');

      sourceSummary.schemasMap.forEach((summary, schema) => {
        const schemaName = (schema as any).schemaName || (schema as any).description || 'Anonymous Schema';
        console.group(`%c${schemaName}: ${summary.totalCount} issues`, 'color: #e74c3c;');

        summary.issues.forEach(issue => {
          const samples = Array.from(issue.receivedSamples);
          const samplesStr = samples.map(s => {
            if (s === null) {return 'null';}
            if (s === undefined) {return 'undefined';}
            return typeof s === 'string' ? `'${s}'` : JSON.stringify(s);
          }).join(', ');

          console.error(`'${issue.path}': Expected '${issue.expected}', received: [${samplesStr}]: ${issue.count} occurrences`);
        });

        if (summary.firstOccurrenceStack) {
          console.groupCollapsed('Call Stack (First occurrence)');
          console.log(summary.firstOccurrenceStack);
          console.groupEnd();
        }

        console.groupEnd();
      });

      console.groupEnd();
    });

    console.groupEnd();
    this.statsMap.clear();
  }

  private normalizePath(path: any[] | undefined): string {
    if (!path || path.length === 0) {return 'root';}
    return path.map(p => {
      const {key} = p;
      if (typeof key === 'number' || (typeof key === 'string' && /^\d+$/.test(key))) {
        return '[]';
      }

      return key;
    })
      .filter(p => p !== undefined)
      .join('.')
      .replaceAll('.[]', '[]') || 'root';
  }

  private pruneReceivedValue(value: any): any {
    if (value === null) {return null;}
    if (value === undefined) {return undefined;}
    if (typeof value === 'object') {
      try {
        const str = JSON.stringify(value);
        if (str.length > 200) {
          return str.slice(0, 200) + '...';
        }

        return value;
      } catch {
        return '[Object]';
      }
    }

    return value;
  }
}

export const schemaLogger = new SchemaLogger();
